<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import { useI18n } from "vue-i18n";
import TruckSelect from "@/components/inputs/TruckSelect.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useValidation } from "@/composables/useValidation";
import type { ChangeTruckStatusData, TruckDto } from "@/api/trucks/dto/truck.dto";
import TruckShortInfo from "@/components/truck/TruckShortInfo.vue";
import { DialogSize } from "@/components/types";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import type { reAssignTruckRequest, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { CurrentRouteStatuses, RouteStatus } from "@/data/trip";
import RouteShortInfo from "@/views/trips/view/components/RouteShortInfo.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import type { VForm } from "vuetify/components";
import { useBaseView } from "@/composables";
import { TruckStatus } from "@/data/truck";
import TruckChangeStatus from "@/components/truck/TruckChangeStatus.vue";

const api = new API();

const { t } = useI18n();
const { requiredValidator } = useValidation();
const { showAPIError } = useError();
const { scrollToError } = useBaseView();

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  tripId: number;
  replacedTruck: TruckDto;
  currentRouteId: number;
  currentRouteStatus: RouteStatus;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "success", replacedTruck: TruckDto): void;
}>();

const assignedTruckId = ref<number | null>(null);
const assignedTruck = ref<TruckDto | null>(null);
const assignedTruckRoutes = ref<TripRouteDto[]>([]);
const replacedTruckRoutes = ref<TripRouteDto[]>([]);
const changeTruckStatusData = ref<ChangeTruckStatusData | null>(null);
const form = ref<VForm | null>(null);
const loading = ref<boolean>(true);
const loadingReassignTruck = ref<boolean>(false);

const selectTitle = computed(() => {
  return `${t("trucks.assigned-truck")}:`;
});

const replacedTruckCurrentRoute = computed(() => {
  return replacedTruckRoutes.value.find((route) => CurrentRouteStatuses.includes(route.status));
});

const replacedTruckPlannedRoutes = computed(() => {
  return replacedTruckRoutes.value.filter((route) => route.status === RouteStatus.planned);
});

const showReplacedTurckChangeStatus = computed(() => {
  return replacedTruckRoutes.value.length <= 1;
});

const showReplacedTruckStatus = computed(() => {
  return replacedTruckCurrentRoute.value && props.currentRouteStatus == RouteStatus.planned;
});

const hasReplacedAnotherCurrentRoute = computed(() => {
  return replacedTruckCurrentRoute.value && replacedTruckCurrentRoute.value.id !== props.currentRouteId;
});

async function getTruckById(id: number) {
  try {
    const response = await api.trucks.findTruckById(id);

    if (response.success && response.data) {
      assignedTruck.value = response.data;
    } else {
      showAPIError(response);
    }
  } catch (error) {
    console.error(error);
  }
}

async function getTruckRoutes(id: number): Promise<TripRouteDto[]> {
  try {
    const response = await api.trucks.getTruckRoutes(id, [
      RouteStatus.planned,
      RouteStatus.inProgress,
      RouteStatus.notStarted,
    ]);

    if (!response.success || !response.data) {
      return [];
    }

    return response.data.routes;
  } catch (error) {
    console.error(error);

    return [];
  }
}

async function reAssignTruck() {
  if (!form.value) {
    return;
  }

  const { valid } = await form.value.validate();

  if (!valid || !assignedTruckId.value) {
    scrollToError();

    return;
  }

  const request: reAssignTruckRequest = {
    newTruckId: assignedTruckId.value,
    currentTruckLocation: changeTruckStatusData.value,
  };

  loadingReassignTruck.value = true;

  try {
    const response = await api.trips.reAssignTruckOnRoute(props.tripId, props.currentRouteId, request);

    if (!response.success) {
      showAPIError(response);

      return;
    }

    emits("update:modelValue", false);
    emits("success", props.replacedTruck);
  } catch (error) {
    console.error(error);
  } finally {
    loadingReassignTruck.value = false;
  }
}

async function getReplacedTruckRoutes() {
  if (props.replacedTruck.id) {
    replacedTruckRoutes.value = await getTruckRoutes(props.replacedTruck.id);
  }

  loading.value = false;
}

watch(assignedTruckId, async (value) => {
  if (!value) {
    return;
  }

  await getTruckById(value);

  assignedTruckRoutes.value = await getTruckRoutes(value);
});

onMounted(async () => {
  await getReplacedTruckRoutes();
});
</script>

<template>
  <omni-dialog
    v-model="model"
    :loading="loading || loadingReassignTruck"
    :primary-action-label="t('general.change')"
    :size="DialogSize.large"
    :title="t('trucks.replace-truck')"
    @primary-action:click="reAssignTruck"
  >
    <v-form ref="form">
      <div class="text-grey-darken-3 font-weight-500">
        {{ t("trucks.assign-truck") }}
      </div>

      <div class="mt-3 text-grey-darken-1">
        {{ t("trucks.select-truck-for-assign") }}
      </div>

      <truck-select
        v-model="assignedTruckId"
        :hide-details="false"
        :label="selectTitle"
        :rules="[requiredValidator]"
        class="mt-4 required"
      />

      <truck-short-info v-if="assignedTruck" :truck="assignedTruck" class="mb-5" data-qa="new-truck-block">
        <alert-message
          v-if="assignedTruck?.status === TruckStatus.InRoute"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-3 text-indigo"
          color="indigo"
          icon-color="indigo"
        >
          {{ t("trucks.truck-has-in-route-status") }}
        </alert-message>
      </truck-short-info>

      <div>{{ t("trucks.replaced-truck") }}</div>

      <div class="d-flex align-center text-grey-darken-1 mt-3 col-gap-1">
        <span>{{ t("general.please-select-status") }}</span>
        <router-link
          v-if="props.replacedTruck.id"
          :to="{ name: 'truck-view', params: { id: props.replacedTruck.id } }"
          class="text-primary text-decoration-none font-weight-500"
          target="_blank"
        >
          Truck {{ replacedTruck.number }}
        </router-link>

        <span>{{ t("general.after-replacement") }}</span>
      </div>

      <truck-short-info
        :hide-status="!showReplacedTruckStatus"
        :truck="replacedTruck"
        class="mt-4"
        data-qa="replaced-truck-block"
      />

      <template v-if="!loading">
        <template v-if="hasReplacedAnotherCurrentRoute && replacedTruckCurrentRoute">
          <div class="text-grey-darken-2 mt-6">Truck {{ replacedTruck.number }} current route:</div>

          <route-short-info :route="replacedTruckCurrentRoute" class="mt-3" data-qa="current-route-block" />
        </template>

        <template v-if="replacedTruckPlannedRoutes.length === 1">
          <div class="text-grey-darken-2 mt-6">Truck {{ replacedTruck.number }} next planned route:</div>

          <route-short-info
            :disabled="currentRouteId === replacedTruckPlannedRoutes[0].id"
            :is-replaced="currentRouteId === replacedTruckPlannedRoutes[0].id"
            :route="replacedTruckPlannedRoutes[0]"
            class="mt-3"
            data-qa="planned-route-block"
          />
        </template>

        <template v-else-if="replacedTruckPlannedRoutes.length > 1">
          <div class="text-grey-darken-2 mt-6">Truck {{ replacedTruck.number }} planned routes:</div>

          <route-short-info
            v-for="route of replacedTruckPlannedRoutes"
            :key="route.id"
            :disabled="currentRouteId === route.id"
            :is-replaced="currentRouteId === route.id"
            :route="route"
            class="mt-3"
          />
        </template>

        <truck-change-status
          v-if="showReplacedTurckChangeStatus"
          v-model="changeTruckStatusData"
          :default-status="TruckStatus.Available"
          :truck="replacedTruck"
          class="mt-6"
        />
      </template>
    </v-form>
  </omni-dialog>
</template>
